// components/models/VideoInputRangeControls.tsx
import React, { useCallback } from 'react';
import { AppTooltip } from '../ui/tooltip';
import RangeControl from '../ui/RangeControl';

type VideoInputRangeParam = 'video_input_start_ms' | 'video_input_end_ms';

interface VideoInputRangeControlsProps {
  startMs?: number | null;
  endMs?: number | null;
  onChange: (name: VideoInputRangeParam, value: number | null) => void;
  className?: string;
}

const msToControlSeconds = (value: number | null | undefined): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const seconds = Math.max(0, value) / 1000;
  return Number.isInteger(seconds) ? seconds : Number(seconds.toFixed(3));
};

const parseInputSecondsToMs = (value: string | number): number | null => {
  const trimmed = String(value).trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.round(parsed * 1000));
};

const VIDEO_INPUT_RANGE_HINT = 'Leave both blank to use the highest visible video track at the current frame. Set one or both times to compose the visible timeline; blank start means 0s, blank finish means timeline end.';
const VIDEO_INPUT_RANGE_TOOLTIP_CLASS = 'max-w-[270px] border border-white/[0.06] bg-[#353535] px-3 py-2 text-xs leading-relaxed text-white/75 shadow-2xl';
const MAX_VIDEO_INPUT_SECONDS = 60 * 60;

const VideoInputRangeControls: React.FC<VideoInputRangeControlsProps> = React.memo(({
  startMs,
  endMs,
  onChange,
  className = '',
}) => {
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.name as VideoInputRangeParam, parseInputSecondsToMs(event.target.value));
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="block text-sm font-medium text-white">Video Input Time</div>
      <div className="grid grid-cols-2 gap-2">
        <AppTooltip
          content={VIDEO_INPUT_RANGE_HINT}
          side="left"
          contentClassName={VIDEO_INPUT_RANGE_TOOLTIP_CLASS}
        >
          <div>
            <RangeControl
              label="Start (s)"
              name="video_input_start_ms"
              min={0}
              max={MAX_VIDEO_INPUT_SECONDS}
              step={0.1}
              value={msToControlSeconds(startMs)}
              onChange={handleChange}
              hideRange
              allowEmpty
              placeholder="blank"
            />
          </div>
        </AppTooltip>
        <AppTooltip
          content={VIDEO_INPUT_RANGE_HINT}
          side="left"
          contentClassName={VIDEO_INPUT_RANGE_TOOLTIP_CLASS}
        >
          <div>
            <RangeControl
              label="Finish (s)"
              name="video_input_end_ms"
              min={0}
              max={MAX_VIDEO_INPUT_SECONDS}
              step={0.1}
              value={msToControlSeconds(endMs)}
              onChange={handleChange}
              hideRange
              allowEmpty
              placeholder="blank"
            />
          </div>
        </AppTooltip>
      </div>
    </div>
  );
});

VideoInputRangeControls.displayName = 'VideoInputRangeControls';

export default VideoInputRangeControls;
